/**
 * La tabla de precios de un producto en renting.
 *
 * Angel, 24-sep-2026: "segun el numero de meses que quiera y tiene un precio
 * u otro". Cada plazo se pacta con la financiera, así que esto es una tabla
 * que se teclea, no una fórmula: lo que no esté aquí no se puede ofrecer.
 *
 * Solo aparece en los productos de tipo renting. Ver `lib/renting.js`.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { motivoParaNoGuardar } from '@/lib/renting';

/** Los plazos que se pactan casi siempre, para no teclearlos a mano. */
const HABITUALES = [24, 36, 48];

export function EditorDePlazos({ plazos, onChange }) {
  const lista = plazos || [];
  const pega = motivoParaNoGuardar(lista);

  const cambiar = (indice, campo, valor) => {
    onChange(lista.map((p, i) => (i === indice ? { ...p, [campo]: valor } : p)));
  };

  const anadir = () => {
    // Propone el siguiente plazo habitual que aún no esté puesto; si ya están
    // todos, uno vacío. Teclear 24, 36 y 48 a mano en cada producto es el
    // tipo de trabajo que se hace mal a la tercera vez.
    const puestos = new Set(lista.map((p) => Number(p.meses)));
    const sugerido = HABITUALES.find((m) => !puestos.has(m));
    onChange([...lista, { meses: sugerido ?? '', cuota: '' }]);
  };

  const quitar = (indice) => onChange(lista.filter((_, i) => i !== indice));

  return (
    <div className="space-y-3 rounded-lg border bg-slate-50 p-3" data-testid="editor-plazos">
      <div className="flex items-center justify-between">
        <Label>Plazos y cuotas *</Label>
        <Button
          type="button" variant="outline" size="sm" onClick={anadir}
          data-testid="anadir-plazo"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Añadir plazo
        </Button>
      </div>

      {lista.length === 0 ? (
        <p className="text-xs text-slate-500">
          Sin plazos no se puede poner este producto en un presupuesto: no
          habría ninguna cuota que elegir.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs text-slate-500">
            <span>Meses</span>
            <span>Cuota al mes (€)</span>
            <span className="w-8" />
          </div>
          {lista.map((plazo, i) => (
            // El índice como clave: estas filas no se reordenan solas, solo
            // se añaden al final y se borran.
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                type="number" min="1" step="1" placeholder="24"
                value={plazo.meses ?? ''}
                onChange={(e) => cambiar(i, 'meses', e.target.value)}
                data-testid={`plazo-meses-${i}`}
              />
              <Input
                type="number" min="0" step="0.01" placeholder="45.00"
                value={plazo.cuota ?? ''}
                onChange={(e) => cambiar(i, 'cuota', e.target.value)}
                data-testid={`plazo-cuota-${i}`}
              />
              <Button
                type="button" variant="ghost" size="sm"
                onClick={() => quitar(i)}
                className="text-red-600 hover:text-red-700 px-2"
                title="Quitar este plazo"
                data-testid={`quitar-plazo-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {pega && lista.length > 0 && (
        <p className="text-xs text-amber-700 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {pega}
        </p>
      )}

      <p className="text-xs text-slate-500">
        La cuota es lo que el cliente paga <strong>cada mes</strong> a la
        entidad que financia el renting. En el presupuesto se elige el plazo y
        sale esta cuota; aquí no se factura nada de esto.
      </p>
    </div>
  );
}

export default EditorDePlazos;
